import PropTypes from 'prop-types';
import { Fragment, useEffect, useMemo, useState, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useOrganization } from 'contexts/OrganizationContext';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { List } from '@mui/material';
// third party
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import NavCollapse from '../NavCollapse';
import NavItem from '../NavItem';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import Transitions from 'ui-component/extended/Transitions';
import { useGetMenuMaster } from 'api/menu';
import OrganizationDropdown from '../../Sidebar/OrganizationDropdown';
// assets
import { motion, AnimatePresence } from 'framer-motion';
import { IconChevronRight, IconMinusVertical } from '@tabler/icons-react';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import DeviceGroupIcon from '../../../../assets/images/device-group-icon.png';
import { GLOBAL_SEARCH_CLIENT_NAV_EVENT } from 'utils/CommonMethods';
// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //

export default function NavGroup({
  item,
  lastItem,
  remItems,
  lastItemId,
  selectedID,
  setSelectedID,
  orgLocData,
  onOrgDropdownOpen,
  showLoaderValue
}) {
  const theme = useTheme();
  const intl = useIntl();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const [openOrgTreeFromSearchWarning, setOpenOrgTreeFromSearchWarning] = useState(false);
  const { menuOrientation, borderRadius } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentItem, setCurrentItem] = useState(item);
  const [orgData, setOrgData] = useState([]);
  const [locData, setLocData] = useState([]);
  const navigate = useNavigate();
  const allOrganizationsLabel = intl.formatMessage({ id: 'allOrganizations' });
  const [selectedType, setSelectedType] = useState(() => {
    const stored = localStorage.getItem('selectedType');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Update label for "All Organizations" type to use translated value
      if (parsed.type === 'All Organizations') {
        return { ...parsed, label: intl.formatMessage({ id: 'allOrganizations' }) };
      }
      return parsed;
    }
    return {
      id: 'All Organizations',
      label: intl.formatMessage({ id: 'allOrganizations' }),
      type: 'All Organizations',
      icon: 'All Organizations'
    };
  });
  localStorage.setItem(
    'selectedLocationId',
    JSON.stringify(localStorage.getItem('selectedLocationId') ? JSON.parse(localStorage.getItem('selectedLocationId')) : [])
  );
  const { updateOrganizationContext, orgId, hierarchyRefreshTrigger, selectedType: contextSelectedType } = useOrganization();
  // Ref to track if navigation was triggered by our dropdown/button (to skip pathname sync)
  const isProgrammaticNavigation = useRef(false);
  // Ref to track if pathname change is from browser back/forward (popstate event)
  const isPopstateNavigation = useRef(false);
  const openMini = Boolean(anchorEl);
  // "All Organizations" selectedType objects reach this component from many writers — localStorage,
  // browser history state, OrganizationContext (header logo, notifications) — and several of those
  // carry a label captured under whatever language was active when they were written. Re-derive the
  // label from intl for display so the hierarchy tree follows the current locale no matter who set it.
  const displaySelectedType = useMemo(
    () => (selectedType?.type === 'All Organizations' ? { ...selectedType, label: allOrganizationsLabel } : selectedType),
    [selectedType, allOrganizationsLabel]
  );
  useEffect(() => {
    /**
     * `id` stays as the API scope id (orgId, location id, groupId) for navigation and storage.
     * `treeRowKey` is a globally unique path key — location.id / groupId can repeat across orgs, which
     * broke virtual tree expansion and connector lines when the same numeric id appeared under different parents.
     */
    const orgNodes = orgLocData.map((org) => ({
      id: org.orgId,
      treeRowKey: `org:${org.orgId}`,
      label: org.orgName,
      type: 'Org',
      icon: <ApartmentOutlinedIcon sx={{ width: '18px' }} />,
      children: org.locations.map((location) => ({
        id: location.id,
        treeRowKey: `org:${org.orgId}:loc:${location.id}`,
        orgName: org.orgName,
        label: location.locationName,
        type: 'Loc',
        orgId: org.orgId,
        icon: <LocationOnOutlinedIcon sx={{ width: '18px' }} />,
        children:
          location?.customGroups?.map((customGroup) => ({
            id: customGroup.groupId,
            treeRowKey: `org:${org.orgId}:loc:${location.id}:grp:${customGroup.groupId}`,
            orgName: org.orgName,
            orgId: org.orgId,
            locationName: location.locationName,
            locationID: location.id,
            label: customGroup.groupName,
            type: 'CustomGroup',
            icon: <Box component="img" src={DeviceGroupIcon} alt="Device group" sx={{ width: '16px', paddingRight: '4px' }} />,
            children: []
          })) || []
      }))
    }));
    const customDataOrg = {
      id: 'All Organizations',
      treeRowKey: 'ao',
      label: allOrganizationsLabel,
      type: 'All Organizations',
      children: orgNodes,
      icon: <ApartmentOutlinedIcon sx={{ width: '18px' }} />
    };
    setOrgData([customDataOrg]);
    setLocData(JSON.parse(localStorage.getItem('selectedLocationId')));
    // Sync selectedType from localStorage when orgLocData changes (e.g., on initial load or data refresh)
    // This ensures the dropdown reflects the persisted selection
    const storedSelectedType = localStorage.getItem('selectedType');
    if (storedSelectedType) {
      const parsed = JSON.parse(storedSelectedType);
      // Update label for "All Organizations" type to use translated value
      if (parsed.type === 'All Organizations') {
        setSelectedType({ ...parsed, label: allOrganizationsLabel });
      } else {
        setSelectedType(parsed);
      }
    }
  }, [orgLocData, allOrganizationsLabel]);

  // Sync local selectedType with context when hierarchyRefreshTrigger changes
  // This ensures the dropdown and breadcrumbs update immediately after saving location settings
  useEffect(() => {
    if (hierarchyRefreshTrigger > 0 && contextSelectedType) {
      setSelectedType(contextSelectedType);
    }
  }, [hierarchyRefreshTrigger, contextSelectedType]);

  // Sync tree dropdown when context changes externally (e.g. global search client navigation)
  useEffect(() => {
    if (!contextSelectedType) return;
    setSelectedType((prev) => {
      if (prev?.id === contextSelectedType.id && prev?.type === contextSelectedType.type && prev?.label === contextSelectedType.label) {
        return prev;
      }
      return contextSelectedType;
    });
  }, [contextSelectedType]);

  useEffect(() => {
    const syncTreeFromGlobalSearch = () => {
      const stored = localStorage.getItem('selectedType');
      if (!stored) return;
      try {
        setSelectedType(JSON.parse(stored));
      } catch {
        // ignore invalid JSON
      }
    };
    window.addEventListener(GLOBAL_SEARCH_CLIENT_NAV_EVENT, syncTreeFromGlobalSearch);
    return () => window.removeEventListener(GLOBAL_SEARCH_CLIENT_NAV_EVENT, syncTreeFromGlobalSearch);
  }, []);

  useEffect(() => {
    // SAFEGUARD: Ensure browser history has state for back button (non-destructive)
    // Only adds state if not already present - doesn't overwrite existing state
    try {
      const existingState = window.history.state;
      const currentStoredSelectedType = localStorage.getItem('selectedType');
      if (!existingState?.organizationState && currentStoredSelectedType) {
        window.history.replaceState(
          {
            ...(existingState || {}),
            organizationState: {
              selectedType: JSON.parse(currentStoredSelectedType),
              scopeId: localStorage.getItem('scopeId') || '0',
              orgId: localStorage.getItem('OrgId'),
              currentNetwork: localStorage.getItem('currentNetwork'),
              savedOrgName: localStorage.getItem('savedOrgName'),
              savedLocationName: localStorage.getItem('savedLocationName'),
              currentDeviceGrpId: localStorage.getItem('currentDeviceGrpId'),
              savedDeviceGrpName: localStorage.getItem('savedDeviceGrpName')
            }
          },
          ''
        );
      }
    } catch (e) {
      /* ignore history errors */
    }
  }, [orgLocData]);

  const navigateToMSPHome = () => {
    const selectedTypeObj = { id: 'All Organizations', label: allOrganizationsLabel, type: 'All Organizations', icon: 'All Organizations' };
    updateOrganizationContext(null, '0', null, selectedTypeObj);
    setSelectedType(selectedTypeObj);
    localStorage.setItem('selectedType', JSON.stringify(selectedTypeObj));
    isProgrammaticNavigation.current = true;
    requestAnimationFrame(() => {
      isProgrammaticNavigation.current = false;
    });
    navigate('./mspHome/dashboard');
    saveStateToHistory({
      selectedType: selectedTypeObj,
      scopeId: '0',
      orgId: null,
      currentNetwork: null,
      savedOrgName: null,
      savedLocationName: null,
      currentDeviceGrpId: null,
      savedDeviceGrpName: null
    });
  };
  const selectedOrg = (id, label, type, item, parentId) => {
    // Mark as programmatic navigation to prevent pathname effect from running
    isProgrammaticNavigation.current = true;
    // Safety: clear flag after React effects have consumed it
    // (pathname effect may not fire if URL doesn't change, e.g. switching between locations)
    requestAnimationFrame(() => {
      isProgrammaticNavigation.current = false;
    });

    if (type == 'Org') {
      localStorage.setItem('OrgId', item.id);
      localStorage.setItem('scopeId', 1);
      localStorage.setItem('savedOrgName', label);
      const orgType = { id: 'Organization-wide', label, type: 'Organization-wide', icon: item.type };
      localStorage.setItem('selectedType', JSON.stringify(orgType));
      updateOrganizationContext(item.id, '1', null, orgType);
      setSelectedType(orgType);
      navigate(`./organization/${encodeURIComponent(label)}/dashboard`);
      saveStateToHistory({
        selectedType: orgType,
        scopeId: '1',
        orgId: item.id,
        currentNetwork: null,
        savedOrgName: label,
        savedLocationName: null,
        currentDeviceGrpId: null,
        savedDeviceGrpName: null
      });
      return;
    }
    // Create selectedType object first
    const selectedTypeObj = { id, label, type, icon: item.type };

    // Update state FIRST before navigation to ensure dropdown updates immediately
    setSelectedType(selectedTypeObj);
    localStorage.setItem('selectedType', JSON.stringify(selectedTypeObj));

    if (type == 'Loc') {
      localStorage.setItem('OrgId', item.orgId);
      localStorage.setItem('scopeId', 2);
      localStorage.setItem('currentNetwork', item.id);
      localStorage.setItem('savedOrgName', item.orgName);
      localStorage.setItem('savedLocationName', label);
      updateOrganizationContext(item.orgId, '2', item.id, selectedTypeObj);
      navigate('./dashboard');
      saveStateToHistory({
        selectedType: selectedTypeObj,
        scopeId: '2',
        orgId: item.orgId,
        currentNetwork: item.id,
        savedOrgName: item.orgName,
        savedLocationName: label,
        currentDeviceGrpId: null,
        savedDeviceGrpName: null
      });
    } else if (type == 'CustomGroup') {
      const parentNetworkId = item?.locationID || parentId?.split('loc:')[1] || '';
      localStorage.setItem('savedLocationName', item?.locationName);
      localStorage.setItem('scopeId', 3);
      localStorage.setItem('OrgId', item?.orgId || orgId);
      localStorage.setItem('currentDeviceGrpId', id);
      localStorage.setItem('currentNetwork', parentNetworkId);
      localStorage.setItem('savedDeviceGrpName', label);
      if (item.orgName) {
        localStorage.setItem('savedOrgName', item.orgName);
      }

      updateOrganizationContext(item?.orgId || orgId, '3', parentNetworkId, selectedTypeObj);
      navigate('./device-group/devices');
      saveStateToHistory({
        selectedType: selectedTypeObj,
        scopeId: '3',
        orgId: item?.orgId || orgId,
        currentNetwork: parentNetworkId,
        savedOrgName: item.orgName || localStorage.getItem('savedOrgName'),
        savedLocationName: item?.locationName,
        currentDeviceGrpId: id,
        savedDeviceGrpName: label
      });
      return;
    } else {
      localStorage.setItem('scopeId', 0);
      const allOrgsType = { id: 'All Organizations', label: allOrganizationsLabel, type: 'All Organizations', icon: 'All Organizations' };
      updateOrganizationContext(null, '0', null, allOrgsType);
      localStorage.removeItem('savedOrgName');
      navigate('./mspHome/dashboard');
      saveStateToHistory({
        selectedType: allOrgsType,
        scopeId: '0',
        orgId: null,
        currentNetwork: null,
        savedOrgName: null,
        savedLocationName: null,
        currentDeviceGrpId: null,
        savedDeviceGrpName: null
      });
    }
  };
  useEffect(() => {
    if (lastItem) {
      if (item.id === lastItemId) {
        const localItem = { ...item };
        const elements = remItems.map((ele) => ele.elements);
        localItem.children = elements.flat(1);
        setCurrentItem(localItem);
      } else {
        setCurrentItem(item);
      }
    }
  }, [item, lastItem, menuOrientation, remItems, lastItemId]);

  const checkOpenForParent = (child, id) => {
    child.forEach((ele) => {
      if (ele.children?.length) {
        checkOpenForParent(ele.children, currentItem.id);
      }
      if (ele?.url && !!matchPath({ path: ele?.link ? ele.link : ele.url, end: true }, pathname)) {
        setSelectedID(id);
      }
    });
  };

  const checkSelectedOnload = (data) => {
    const childrens = data.children ? data.children : [];
    childrens.forEach((itemCheck) => {
      if (itemCheck?.children?.length) {
        checkOpenForParent(itemCheck.children, currentItem.id);
      }
      if (itemCheck?.url && !!matchPath({ path: itemCheck?.link ? itemCheck.link : itemCheck.url, end: true }, pathname)) {
        setSelectedID(currentItem.id);
      }
    });

    if (data?.url && !!matchPath({ path: data?.link ? data.link : data.url, end: true }, pathname)) {
      setSelectedID(currentItem.id);
    }
  };

  // Store navigation state in browser history when navigating programmatically
  // This allows us to restore the exact state when using browser back/forward
  const saveStateToHistory = (stateData) => {
    const currentState = window.history.state || {};
    window.history.replaceState({ ...currentState, organizationState: stateData }, '');
  };

  // Handle browser back/forward navigation via popstate event
  // This runs BEFORE React re-renders, ensuring context is correct when dashboard loads
  useEffect(() => {
    const handlePopState = (event) => {
      // Mark this as a popstate navigation so pathname effect knows to skip
      isPopstateNavigation.current = true;
      // Safety: clear flag after React effects have consumed it
      // (pathname effect won't fire when back/forward navigates between same-URL entries)
      requestAnimationFrame(() => {
        isPopstateNavigation.current = false;
      });

      const currentPath = window.location.pathname;
      const savedState = event.state?.organizationState;

      // If we have saved state from history, restore it directly
      if (savedState) {
        setSelectedType(savedState.selectedType);
        localStorage.setItem('selectedType', JSON.stringify(savedState.selectedType));
        localStorage.setItem('scopeId', savedState.scopeId || '0');
        if (savedState.orgId) localStorage.setItem('OrgId', savedState.orgId);
        if (savedState.currentNetwork) localStorage.setItem('currentNetwork', savedState.currentNetwork);
        if (savedState.savedOrgName) localStorage.setItem('savedOrgName', savedState.savedOrgName);
        if (savedState.savedLocationName) localStorage.setItem('savedLocationName', savedState.savedLocationName);
        if (savedState.currentDeviceGrpId) localStorage.setItem('currentDeviceGrpId', savedState.currentDeviceGrpId);
        if (savedState.savedDeviceGrpName) localStorage.setItem('savedDeviceGrpName', savedState.savedDeviceGrpName);

        updateOrganizationContext(
          savedState.orgId || null,
          savedState.scopeId || '0',
          savedState.currentNetwork || null,
          savedState.selectedType
        );
        return;
      }

      // Fallback: Determine state from URL path
      // Check if we're at MSP home level
      if (currentPath === '/mspHome/dashboard' || currentPath.startsWith('/mspHome/')) {
        const allOrgsType = { id: 'All Organizations', label: allOrganizationsLabel, type: 'All Organizations', icon: 'All Organizations' };
        setSelectedType(allOrgsType);
        localStorage.setItem('selectedType', JSON.stringify(allOrgsType));
        localStorage.setItem('scopeId', '0');
        updateOrganizationContext(null, '0', null, allOrgsType);
      }
      // Check if we're at organization level
      else if (currentPath.startsWith('/organization/') && currentPath.includes('/dashboard')) {
        const pathParts = currentPath.split('/');
        const orgIndex = pathParts.indexOf('organization');
        if (orgIndex !== -1 && pathParts.length > orgIndex + 1) {
          const orgName = decodeURIComponent(pathParts[orgIndex + 1]);
          const orgType = { id: 'Organization-wide', label: orgName, type: 'Organization-wide', icon: 'Org' };
          setSelectedType(orgType);
          localStorage.setItem('selectedType', JSON.stringify(orgType));
          localStorage.setItem('savedOrgName', orgName);
          localStorage.setItem('scopeId', '1');
          // Find orgId from orgData
          const orgNode = orgData[0]?.children?.find((org) => org.label === orgName);
          if (orgNode) {
            localStorage.setItem('OrgId', orgNode.id);
            updateOrganizationContext(orgNode.id, '1', null, orgType);
          }
        }
      }
      // Check if we're at location level
      else if (currentPath === '/dashboard' || (currentPath.startsWith('/dashboard') && !currentPath.includes('/organization/'))) {
        let locId = localStorage.getItem('currentNetwork');
        let locName = localStorage.getItem('savedLocationName');
        const storedOrgId = localStorage.getItem('OrgId');

        // SAFEGUARD: If direct localStorage values missing, try to get from selectedType
        if (!locId || !locName) {
          try {
            const storedSelectedType = localStorage.getItem('selectedType');
            if (storedSelectedType) {
              const parsed = JSON.parse(storedSelectedType);
              if (parsed.type === 'Loc') {
                locId = locId || parsed.id;
                locName = locName || parsed.label;
              }
            }
          } catch (e) {
            /* ignore */
          }
        }

        if (locName && locId) {
          const locType = {
            id: locId,
            label: locName,
            type: 'Loc',
            icon: 'Loc'
          };
          setSelectedType(locType);
          localStorage.setItem('selectedType', JSON.stringify(locType));
          localStorage.setItem('scopeId', '2');
          // Also ensure these are saved for future back navigations
          localStorage.setItem('currentNetwork', locId);
          localStorage.setItem('savedLocationName', locName);
          updateOrganizationContext(storedOrgId, '2', locId, locType);
        }
      }
      // Check if we're at device group level
      else if (currentPath.startsWith('/device-group/')) {
        let dgId = localStorage.getItem('currentDeviceGrpId');
        let dgName = localStorage.getItem('savedDeviceGrpName');
        const storedOrgId = localStorage.getItem('OrgId');
        const storedNetworkId = localStorage.getItem('currentNetwork');

        // SAFEGUARD: If direct localStorage values missing, try to get from selectedType
        if (!dgId || !dgName) {
          try {
            const storedSelectedType = localStorage.getItem('selectedType');
            if (storedSelectedType) {
              const parsed = JSON.parse(storedSelectedType);
              if (parsed.type === 'CustomGroup') {
                dgId = dgId || parsed.id;
                dgName = dgName || parsed.label;
              }
            }
          } catch (e) {
            /* ignore */
          }
        }

        if (dgName && dgId) {
          const dgType = {
            id: dgId,
            label: dgName,
            type: 'CustomGroup',
            icon: 'CustomGroup'
          };
          setSelectedType(dgType);
          localStorage.setItem('selectedType', JSON.stringify(dgType));
          localStorage.setItem('scopeId', '3');
          // Also ensure these are saved for future back navigations
          localStorage.setItem('currentDeviceGrpId', dgId);
          localStorage.setItem('savedDeviceGrpName', dgName);
          updateOrganizationContext(storedOrgId, '3', storedNetworkId, dgType);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrganizationContext, orgData]);

  // Handle pathname changes from different sources:
  // 1. Our dropdown/button (isProgrammaticNavigation = true) - just save state to history
  // 2. Browser back/forward (isPopstateNavigation = true) - already handled by popstate event
  // 3. Breadcrumbs/links (both flags false) - sync dropdown based on URL
  useEffect(() => {
    // If this was navigation from our dropdown/button, just save state and skip
    if (isProgrammaticNavigation.current) {
      isProgrammaticNavigation.current = false;

      // Save current state to history for browser back restoration
      const currentState = {
        selectedType: JSON.parse(localStorage.getItem('selectedType') || '{}'),
        scopeId: localStorage.getItem('scopeId'),
        orgId: localStorage.getItem('OrgId'),
        currentNetwork: localStorage.getItem('currentNetwork'),
        savedOrgName: localStorage.getItem('savedOrgName'),
        savedLocationName: localStorage.getItem('savedLocationName'),
        currentDeviceGrpId: localStorage.getItem('currentDeviceGrpId'),
        savedDeviceGrpName: localStorage.getItem('savedDeviceGrpName')
      };
      saveStateToHistory(currentState);
      return;
    }

    // If this was browser back/forward, popstate already handled it
    if (isPopstateNavigation.current) {
      isPopstateNavigation.current = false;
      return;
    }

    // This is breadcrumb/link navigation - sync local selectedType state with localStorage
    // The breadcrumb component may have already updated localStorage and context,
    // but we need to update NavGroup's LOCAL selectedType state for the dropdown to reflect the change
    const storedSelectedType = localStorage.getItem('selectedType') ? JSON.parse(localStorage.getItem('selectedType')) : null;

    // Check if we're at MSP home level
    if (pathname === '/mspHome/dashboard' || pathname.startsWith('/mspHome/')) {
      const allOrgsType = { id: 'All Organizations', label: allOrganizationsLabel, type: 'All Organizations', icon: 'All Organizations' };
      // Always sync local state - breadcrumb may have already updated localStorage
      if (storedSelectedType?.type === 'All Organizations') {
        // localStorage already correct (breadcrumb updated it), just sync local state
        setSelectedType(storedSelectedType);
      } else {
        // Need full update
        setSelectedType(allOrgsType);
        localStorage.setItem('selectedType', JSON.stringify(allOrgsType));
        localStorage.setItem('scopeId', '0');
        updateOrganizationContext(null, '0', null, allOrgsType);
      }
      // Save to history for browser back
      saveStateToHistory({
        selectedType: allOrgsType,
        scopeId: '0',
        orgId: null,
        currentNetwork: null,
        savedOrgName: null,
        savedLocationName: null,
        currentDeviceGrpId: null,
        savedDeviceGrpName: null
      });
    }
    // Check if we're at organization level
    else if (pathname.startsWith('/organization/')) {
      const pathParts = pathname.split('/');
      const orgIndex = pathParts.indexOf('organization');
      if (orgIndex !== -1 && pathParts.length > orgIndex + 1) {
        const orgName = decodeURIComponent(pathParts[orgIndex + 1]);
        const orgType = { id: 'Organization-wide', label: orgName, type: 'Organization-wide', icon: 'Org' };

        // Always sync local state
        if (storedSelectedType?.type === 'Organization-wide' && storedSelectedType?.label === orgName) {
          // localStorage already correct (breadcrumb updated it), just sync local state.
          setSelectedType(storedSelectedType);
          // Sync the context scope too - a matching local selectedType does not mean the
          // OrganizationContext is at org scope (it may still be the default scope 0).
          const storedOrgIdForSync = localStorage.getItem('OrgId');
          if (storedOrgIdForSync) {
            updateOrganizationContext(storedOrgIdForSync, '1', null, storedSelectedType);
          }
        } else {
          // Need full update
          setSelectedType(orgType);
          localStorage.setItem('selectedType', JSON.stringify(orgType));
          localStorage.setItem('savedOrgName', orgName);
          localStorage.setItem('scopeId', '1');

          // Find orgId from orgData
          const orgNode = orgData[0]?.children?.find((org) => org.label === orgName);
          if (orgNode) {
            localStorage.setItem('OrgId', orgNode.id);
            updateOrganizationContext(orgNode.id, '1', null, orgType);
          }
        }
        // Save to history for browser back
        const savedOrgId = localStorage.getItem('OrgId');
        saveStateToHistory({
          selectedType: orgType,
          scopeId: '1',
          orgId: savedOrgId,
          currentNetwork: null,
          savedOrgName: orgName,
          savedLocationName: null,
          currentDeviceGrpId: null,
          savedDeviceGrpName: null
        });
      }
    }
    // Check if we're at location level (dashboard without organization prefix)
    else if (
      pathname === '/dashboard' ||
      (pathname.startsWith('/') &&
        !pathname.startsWith('/mspHome') &&
        !pathname.startsWith('/organization') &&
        !pathname.startsWith('/device-group'))
    ) {
      const storedNetworkId = localStorage.getItem('currentNetwork');
      const storedLocationName = localStorage.getItem('savedLocationName');
      const storedOrgId = localStorage.getItem('OrgId');

      // Always sync local state
      if (storedSelectedType?.type === 'Loc') {
        // localStorage already correct, just sync local state.
        setSelectedType(storedSelectedType);
        // A matching local selectedType does NOT guarantee the OrganizationContext scope is
        // synced: a direct-to-location login (persistPostLoginScope) writes localStorage
        // without touching context, leaving it at the default scope 0. Sync it so the
        // dashboard summaries query the location (scope 2) rather than MSP (52013).
        if (storedLocationName && storedNetworkId) {
          updateOrganizationContext(storedOrgId, '2', storedNetworkId, storedSelectedType);
        }
      } else if (storedLocationName && storedNetworkId) {
        // Need full update
        const locType = {
          id: storedNetworkId,
          label: storedLocationName,
          type: 'Loc',
          icon: 'Loc'
        };
        setSelectedType(locType);
        localStorage.setItem('selectedType', JSON.stringify(locType));
        localStorage.setItem('scopeId', '2');
        updateOrganizationContext(storedOrgId, '2', storedNetworkId, locType);
      }
      // Save to history for browser back
      if (storedLocationName && storedNetworkId) {
        saveStateToHistory({
          selectedType:
            storedSelectedType?.type === 'Loc'
              ? storedSelectedType
              : { id: storedNetworkId, label: storedLocationName, type: 'Loc', icon: 'Loc' },
          scopeId: '2',
          orgId: storedOrgId,
          currentNetwork: storedNetworkId,
          savedOrgName: localStorage.getItem('savedOrgName'),
          savedLocationName: storedLocationName,
          currentDeviceGrpId: null,
          savedDeviceGrpName: null
        });
      }
    }
    // Check if we're at device group level
    else if (pathname.startsWith('/device-group/')) {
      const storedDeviceGrpId = localStorage.getItem('currentDeviceGrpId');
      const storedDeviceGrpName = localStorage.getItem('savedDeviceGrpName');
      const storedOrgId = localStorage.getItem('OrgId');
      const storedNetworkId = localStorage.getItem('currentNetwork');

      // Always sync local state
      if (storedSelectedType?.type === 'CustomGroup') {
        // localStorage already correct, just sync local state.
        setSelectedType(storedSelectedType);
        // Sync the context scope too (see location/org branches: a matching local
        // selectedType does not guarantee the OrganizationContext scope is synced).
        if (storedDeviceGrpId) {
          updateOrganizationContext(storedOrgId, '3', storedNetworkId, storedSelectedType);
        }
      } else if (storedDeviceGrpName && storedDeviceGrpId) {
        // Need full update
        const dgType = {
          id: storedDeviceGrpId,
          label: storedDeviceGrpName,
          type: 'CustomGroup',
          icon: 'CustomGroup'
        };
        setSelectedType(dgType);
        localStorage.setItem('selectedType', JSON.stringify(dgType));
        localStorage.setItem('scopeId', '3');
        updateOrganizationContext(storedOrgId, '3', storedNetworkId, dgType);
      }
      // Save to history for browser back
      if (storedDeviceGrpName && storedDeviceGrpId) {
        saveStateToHistory({
          selectedType:
            storedSelectedType?.type === 'CustomGroup'
              ? storedSelectedType
              : { id: storedDeviceGrpId, label: storedDeviceGrpName, type: 'CustomGroup', icon: 'CustomGroup' },
          scopeId: '3',
          orgId: storedOrgId,
          currentNetwork: storedNetworkId,
          savedOrgName: localStorage.getItem('savedOrgName'),
          savedLocationName: localStorage.getItem('savedLocationName'),
          currentDeviceGrpId: storedDeviceGrpId,
          savedDeviceGrpName: storedDeviceGrpName
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // keep selected-menu on page load and use for horizontal menu close on change routes
  useEffect(() => {
    checkSelectedOnload(currentItem);
    if (openMini) setAnchorEl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, currentItem]);

  const handleClick = (event) => {
    if (!openMini) {
      setAnchorEl(event?.currentTarget);
      // Trigger refresh of Org/Loc data when dropdown opens
      if (typeof onOrgDropdownOpen === 'function') {
        onOrgDropdownOpen();
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const Icon = currentItem?.icon;
  const itemIcon = currentItem?.icon ? <Icon stroke={1.5} size="20px" /> : null;

  // menu list collapse & items
  const items = currentItem.children?.map((menu) => {
    switch (menu?.type) {
      case 'collapse':
        return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id} />;
      case 'item':
        // Add disabled property to menu object
        const menuWithDisabled = {
          ...menu,
          disabled: false
        };
        const tooltipMessage = '';
        return <NavItem key={menu.id} item={menuWithDisabled} level={1} tooltipMessage={tooltipMessage} />;
      default:
        return (
          <Typography key={menu?.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const moreItems = remItems.map((itemRem, i) => (
    <Fragment key={i}>
      {itemRem.url ? (
        <NavItem item={itemRem} level={1} />
      ) : (
        itemRem.title && (
          <Typography variant="caption" sx={{ pl: 2 }}>
            {itemRem.title} {itemRem.url}
          </Typography>
        )
      )}
      {itemRem?.elements?.map((menu) => {
        switch (menu?.type) {
          case 'collapse':
            return <NavCollapse key={menu.id} menu={menu} level={1} parentId={currentItem.id} />;
          case 'item':
            return <NavItem key={menu.id} item={menu} level={1} />;
          default:
            return (
              <Typography key={menu.id} variant="h6" color="error" align="center">
                Menu Items Error
              </Typography>
            );
        }
      })}
    </Fragment>
  ));

  const popperId = openMini ? `group-pop-${item.id}` : undefined;
  const isSelected = selectedID === currentItem.id;

  return (
    <>
      {!isHorizontal ? (
        <>
          <List
            disablePadding={!drawerOpen}
            sx={{
              marginTop: { xs: '10px', md: '30px', lg: '35px' },
              paddingBottom: '1px'
            }}
            id="groupList"
            subheader={
              currentItem.title &&
              drawerOpen && (
                <>
                  <Typography
                    id="groupMenu-Allorganization"
                    variant="caption"
                    gutterBottom
                    sx={{ display: 'block', ...theme.typography.menuCaption }}
                  >
                    {currentItem.caption && (
                      <Typography
                        id="groupSubMenu-Allorganization"
                        variant="caption"
                        gutterBottom
                        sx={{ display: 'block', ...theme.typography.subMenuCaption }}
                      >
                        <FormattedMessage id={currentItem.caption} />
                      </Typography>
                    )}
                  </Typography>
                </>
              )
            }
          >
            <OrganizationDropdown
              key={`org-dropdown-${displaySelectedType?.id}-${displaySelectedType?.type}-${displaySelectedType?.label}`}
              drawerOpen={drawerOpen}
              calledFrom="Org"
              treeData={orgData}
              selectedOrg={selectedOrg}
              organizationName={displaySelectedType}
              navigateToMSPHome={navigateToMSPHome}
              onOpen={onOrgDropdownOpen}
              showLoaderValue={showLoaderValue}
              openFromSearchWarning={openOrgTreeFromSearchWarning}
              onOpenFromSearchWarningHandled={() => setOpenOrgTreeFromSearchWarning(false)}
            />
          </List>

          <>
            <List
              disablePadding={!drawerOpen}
              sx={{
                opacity: locData.length > 0 ? 1 : 0.7, // Greyed-out effect
                pointerEvents: locData.length > 0 ? '' : 'none', // Prevent interactions
                padding: 0
              }}
              id="locList"
              subheader={
                currentItem.title &&
                drawerOpen && (
                  <>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`${selectedType.type}-${currentItem.caption}-${selectedOrg?.id ?? selectedOrg}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Typography
                          id="locMenuCaption"
                          gutterBottom
                          component={motion.div}
                          initial={{
                            scale: 0.95,
                            color: theme.palette.grey[600],
                            fontSize: '0.9rem'
                          }}
                          animate={{
                            scale: [1, 1.08, 1],
                            color: [theme.palette.grey[900], theme.palette.grey[900]],
                            fontSize: ['0.9rem', '1rem', '0.92rem']
                          }}
                          transition={{ duration: 0.6, ease: 'easeInOut' }}
                          sx={{
                            display: 'block',
                            ...theme.typography.menuCaption
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                            <FormattedMessage
                              id={
                                selectedType.type == 'Loc'
                                  ? 'Location Menu'
                                  : selectedType.type == 'CustomGroup'
                                    ? 'Device Group Menu'
                                    : 'Organization Menu'
                              }
                            />
                          </Typography>

                          {currentItem.caption && (
                            <Typography
                              id="locSubMenuCaption"
                              variant="caption"
                              gutterBottom
                              component={motion.div}
                              animate={{ color: 'inherit' }}
                              sx={{
                                display: 'block',
                                ...theme.typography.subMenuCaption
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                                <FormattedMessage id={currentItem.caption} />
                              </Typography>
                            </Typography>
                          )}
                        </Typography>
                      </motion.div>
                    </AnimatePresence>
                  </>
                )
              }
            ></List>
            <List id="LocListData" sx={{ pt: 0 }}>
              {selectedType.type == 'All Organizations'
                ? items.filter((route) => route.key.split('-')[1] == 'mspLevel')
                : selectedType.type == 'Loc'
                  ? items.filter((route) => route.key.split('-')[1] == 'loc')
                  : selectedType.type == 'CustomGroup'
                    ? items.filter((route) => route.key.split('-')[1] == 'customgroup')
                    : items.filter((route) => route.key.split('-')[1] == 'parOrg')}
            </List>
          </>
        </>
      ) : (
        <List>
          <ListItemButton
            selected={isSelected}
            sx={{
              borderRadius: `${borderRadius}px`,
              p: 1,
              my: 0.5,
              mr: 1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'inherit'
            }}
            onMouseEnter={handleClick}
            onClick={handleClick}
            onMouseLeave={handleClose}
            aria-describedby={popperId}
            id={`nav-group-list-item-button-${item.id}`}
            className={anchorEl ? 'Mui-selected' : ''}
          >
            {itemIcon && (
              <ListItemIcon id={`nav-group-list-item-icon-${item.id}`} sx={{ minWidth: 36 }}>
                {currentItem.id === lastItemId ? <IconMinusVertical stroke={1.5} size="20px" /> : itemIcon}
              </ListItemIcon>
            )}
            <ListItemText
              sx={{ mr: 1, mb: 0.25 }}
              id={`nav-group-list-item-text-${item.id}`}
              primary={
                <Typography id={`nav-group-list-item-text-typography-${item.id}`} variant={isSelected ? 'h5' : 'body1'} color="inherit">
                  {currentItem.id === lastItemId ? (
                    <FormattedMessage id="more-items" />
                  ) : currentItem.title ? (
                    <FormattedMessage id={currentItem.title} />
                  ) : (
                    ''
                  )}
                </Typography>
              }
            />
            {<IconChevronRight stroke={1.5} size="16px" />}

            {anchorEl && (
              <Popper
                id={popperId}
                open={openMini}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{
                  overflow: 'visible',
                  zIndex: 2001,
                  minWidth: 180,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 5,
                    left: 32,
                    width: 12,
                    height: 12,
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 120,
                    borderWidth: '6px',
                    borderStyle: 'solid',
                    borderTopColor: 'background.paper',
                    borderLeftColor: 'background.paper',
                    borderRightColor: 'transparent',
                    borderBottomColor: 'transparent'
                  }
                }}
              >
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper
                      sx={{
                        mt: 0.5,
                        py: 1.25,
                        boxShadow: theme.shadows[8],
                        backgroundImage: 'none'
                      }}
                      id={`nav-group-paper-${item.id}`}
                    >
                      <ClickAwayListener onClickAway={handleClose}>
                        <Box
                          sx={{
                            minWidth: 200,
                            maxHeight: 'calc(100vh - 170px)',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              opacity: 0,
                              width: 4,
                              '&:hover': {
                                opacity: 0.7
                              }
                            },
                            '&::-webkit-scrollbar-track': {
                              bgcolor: 'transparent'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              bgcolor: 'divider',
                              borderRadius: 4
                            }
                          }}
                          id={`nav-group-box-${item.id}`}
                        >
                          {currentItem.id !== lastItemId ? items : moreItems}
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </Popper>
            )}
          </ListItemButton>
        </List>
      )}
    </>
  );
}

NavGroup.propTypes = {
  item: PropTypes.any,
  lastItem: PropTypes.number,
  remItems: PropTypes.array,
  lastItemId: PropTypes.string,
  selectedID: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
